import {
	CalendarSessionColorStatus,
	DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
	DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER,
	EnrollmentStatus,
	type FeatFlagCalendarSessionColorLegend,
	type FeatFlagCalendarSessionEnrollmentStatusFilter,
	FeatFlagCalendarSessionTitleViewMode,
	FeatFlagEnrollmentScheduleSelectionMode,
	FeatFlagScheduleOverlapValidation,
} from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { RotateCcw, Settings } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	fetchOrganizationFeaturesQueryOptions,
	useUpdateCalendarColorLegend,
	useUpdateCalendarEnrollmentStatusFilter,
	useUpdateCalendarSessionTitle,
	useUpdateEnrollmentScheduleSelection,
	useUpdateScheduleOverlapValidation,
} from "@/api/organizations";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationSettingsNavigation } from "@/routes/_auth/app/_app/org/$orgId/settings/-components/OrganizationSettingsNavigation";
import { getContrastingTextColor } from "@/utils/utils";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/features/",
)({
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);

		return { userId, token };
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useTranslation();
	const { userId, token } = Route.useLoaderData();
	const { orgId } = Route.useParams();
	// Fetch current organization features
	const { data: features } = useSuspenseQuery(
		fetchOrganizationFeaturesQueryOptions(userId, token, orgId),
	);

	// Local state for form
	const [enrollmentScheduleMode, setEnrollmentScheduleMode] = useState(
		features.enrollment.scheduleSelection.mode,
	);
	const [calendarSessionTitleViewMode, setCalendarSessionTitleViewMode] =
		useState(features.attendance.calendar.sessionTitleViewMode);
	const [colorLegend, setColorLegend] =
		useState<FeatFlagCalendarSessionColorLegend>(
			features.attendance.calendar.colorLegend ??
				DEFAULT_CALENDAR_SESSION_COLOR_LEGEND,
		);
	const [enrollmentStatusFilter, setEnrollmentStatusFilter] =
		useState<FeatFlagCalendarSessionEnrollmentStatusFilter>(
			features.attendance.calendar.enrollmentStatusFilter ??
				DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER,
		);
	const [scheduleOverlapMode, setScheduleOverlapMode] = useState(
		features.schedule?.overlapValidation ??
			FeatFlagScheduleOverlapValidation.BLOCK,
	);

	// Individual mutations for each feature flag
	const updateEnrollmentMutation = useUpdateEnrollmentScheduleSelection();
	const updateCalendarTitleMutation = useUpdateCalendarSessionTitle();
	const updateColorLegendMutation = useUpdateCalendarColorLegend();
	const updateEnrollmentStatusFilterMutation =
		useUpdateCalendarEnrollmentStatusFilter();
	const updateScheduleOverlapMutation = useUpdateScheduleOverlapValidation();

	// Handlers for color legend
	const handleColorChange = useCallback(
		(status: CalendarSessionColorStatus, color: string) => {
			setColorLegend((prev) => ({ ...prev, [status]: color }));
		},
		[],
	);

	const handleResetColors = useCallback(() => {
		setColorLegend(DEFAULT_CALENDAR_SESSION_COLOR_LEGEND);
	}, []);

	// Handler for enrollment status filter
	const handleDefaultStatusToggle = useCallback((status: string) => {
		setEnrollmentStatusFilter((prev) => ({
			...prev,
			defaultStatuses: prev.defaultStatuses.includes(status)
				? prev.defaultStatuses.filter((s) => s !== status)
				: [...prev.defaultStatuses, status],
		}));
	}, []);

	const originalColorLegend =
		features.attendance.calendar.colorLegend ??
		DEFAULT_CALENDAR_SESSION_COLOR_LEGEND;
	const originalEnrollmentStatusFilter =
		features.attendance.calendar.enrollmentStatusFilter ??
		DEFAULT_CALENDAR_SESSION_ENROLLMENT_STATUS_FILTER;

	const hasEnrollmentModeChanges =
		enrollmentScheduleMode !== features.enrollment.scheduleSelection.mode;
	const hasCalendarTitleChanges =
		calendarSessionTitleViewMode !==
		features.attendance.calendar.sessionTitleViewMode;
	const hasColorLegendChanges = Object.values(CalendarSessionColorStatus).some(
		(status) => colorLegend[status] !== originalColorLegend[status],
	);
	const hasEnrollmentStatusFilterChanges =
		JSON.stringify(enrollmentStatusFilter.defaultStatuses.sort()) !==
		JSON.stringify(originalEnrollmentStatusFilter.defaultStatuses.sort());
	const hasScheduleOverlapChanges =
		scheduleOverlapMode !==
		(features.schedule?.overlapValidation ??
			FeatFlagScheduleOverlapValidation.BLOCK);

	const hasChanges =
		hasEnrollmentModeChanges ||
		hasCalendarTitleChanges ||
		hasColorLegendChanges ||
		hasEnrollmentStatusFilterChanges ||
		hasScheduleOverlapChanges;

	// Save only the features that have changed
	const handleSave = () => {
		if (hasEnrollmentModeChanges) {
			updateEnrollmentMutation.mutate({
				token,
				organizationId: orgId,
				value: enrollmentScheduleMode,
			});
		}
		if (hasCalendarTitleChanges) {
			updateCalendarTitleMutation.mutate({
				token,
				organizationId: orgId,
				value: calendarSessionTitleViewMode,
			});
		}
		if (hasColorLegendChanges) {
			updateColorLegendMutation.mutate({
				token,
				organizationId: orgId,
				value: colorLegend,
			});
		}
		if (hasEnrollmentStatusFilterChanges) {
			updateEnrollmentStatusFilterMutation.mutate({
				token,
				organizationId: orgId,
				value: enrollmentStatusFilter,
			});
		}
		if (hasScheduleOverlapChanges) {
			updateScheduleOverlapMutation.mutate({
				token,
				organizationId: orgId,
				value: scheduleOverlapMode,
			});
		}
	};

	const isSaving =
		updateEnrollmentMutation.isPending ||
		updateCalendarTitleMutation.isPending ||
		updateColorLegendMutation.isPending ||
		updateEnrollmentStatusFilterMutation.isPending ||
		updateScheduleOverlapMutation.isPending;

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("orgSettings.title")}
				description={t("orgSettings.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="organization-settings-container"
			>
				{/* Side Navigation */}
				<OrganizationSettingsNavigation activeTab="features" />

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="organization-settings-main-content-area"
				>
					{/* Enrollment Settings Card */}
					<Card className="mb-4 md:mb-6">
						<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-4">
							<CardTitle className="flex items-center space-x-2 text-sm md:text-base">
								<Settings className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								<span>{t("orgSettings.features.enrollment.title")}</span>
							</CardTitle>
							<CardDescription className="text-xs md:text-sm">
								{t("orgSettings.features.enrollment.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4 md:space-y-6 px-3 md:px-6 pb-3 md:pb-6">
							{/* Schedule Selection Mode */}
							<div className="space-y-3 md:space-y-4">
								<div>
									<Label className="text-sm md:text-base font-medium">
										{t(
											"orgSettings.features.enrollment.scheduleSelection.title",
										)}
									</Label>
									<p className="text-xs md:text-sm text-base-content/60 mt-1">
										{t(
											"orgSettings.features.enrollment.scheduleSelection.description",
										)}
									</p>
								</div>

								<RadioGroup
									value={enrollmentScheduleMode}
									onValueChange={(value) =>
										setEnrollmentScheduleMode(
											value as FeatFlagEnrollmentScheduleSelectionMode,
										)
									}
									className="space-y-3 md:space-y-4"
								>
									{Object.values(FeatFlagEnrollmentScheduleSelectionMode).map(
										(mode) => (
											<div
												key={mode}
												className="flex items-start space-x-2 md:space-x-3"
											>
												<RadioGroupItem
													value={mode}
													id={mode}
													className="mt-0.5 md:mt-1"
												/>
												<div className="flex-1 min-w-0">
													<Label
														htmlFor={mode}
														className="text-xs md:text-sm font-medium cursor-pointer"
													>
														{t(
															`orgSettings.features.enrollment.scheduleSelection.modes.${mode}`,
														)}
													</Label>
													<p className="text-xs text-base-content/60 mt-0.5 md:mt-1">
														{t(
															`orgSettings.features.enrollment.scheduleSelection.modeDescriptions.${mode}`,
														)}
													</p>
												</div>
											</div>
										),
									)}
								</RadioGroup>
							</div>
						</CardContent>
					</Card>

					{/* Attendance Settings Card */}
					<Card className="mb-4 md:mb-6">
						<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-4">
							<CardTitle className="flex items-center space-x-2 text-sm md:text-base">
								<Settings className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								<span>{t("orgSettings.features.attendance.title")}</span>
							</CardTitle>
							<CardDescription className="text-xs md:text-sm">
								{t("orgSettings.features.attendance.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4 md:space-y-6 px-3 md:px-6 pb-3 md:pb-6">
							{/* Calendar Session Title View Mode */}
							<div className="space-y-3 md:space-y-4">
								<div>
									<Label className="text-sm md:text-base font-medium">
										{t(
											"orgSettings.features.attendance.calendarSessionTitleViewMode.title",
										)}
									</Label>
									<p className="text-xs md:text-sm text-base-content/60 mt-1">
										{t(
											"orgSettings.features.attendance.calendarSessionTitleViewMode.description",
										)}
									</p>
								</div>

								<RadioGroup
									value={calendarSessionTitleViewMode}
									onValueChange={(value) =>
										setCalendarSessionTitleViewMode(
											value as FeatFlagCalendarSessionTitleViewMode,
										)
									}
									className="space-y-3 md:space-y-4"
								>
									{Object.values(FeatFlagCalendarSessionTitleViewMode).map(
										(mode) => (
											<div
												key={mode}
												className="flex items-start space-x-2 md:space-x-3"
											>
												<RadioGroupItem
													value={mode}
													id={`calendar-${mode}`}
													className="mt-0.5 md:mt-1"
												/>
												<div className="flex-1 min-w-0">
													<Label
														htmlFor={`calendar-${mode}`}
														className="text-xs md:text-sm font-medium cursor-pointer"
													>
														{t(
															`orgSettings.features.attendance.calendarSessionTitleViewMode.modes.${mode}`,
														)}
													</Label>
													<p className="text-xs text-base-content/60 mt-0.5 md:mt-1">
														{t(
															`orgSettings.features.attendance.calendarSessionTitleViewMode.modeDescriptions.${mode}`,
														)}
													</p>
												</div>
											</div>
										),
									)}
								</RadioGroup>
							</div>

							{/* Color Legend Section */}
							<div className="space-y-3 md:space-y-4 pt-4 border-t border-base-300">
								<div>
									<Label className="text-sm md:text-base font-medium">
										{t("orgSettings.features.attendance.colorLegend.title")}
									</Label>
									<p className="text-xs md:text-sm text-base-content/60 mt-1">
										{t(
											"orgSettings.features.attendance.colorLegend.description",
										)}
									</p>
								</div>

								<div className="grid gap-3">
									{Object.values(CalendarSessionColorStatus).map((status) => (
										<div key={status} className="flex items-center gap-3">
											<Input
												type="color"
												value={colorLegend[status]}
												onChange={(e) =>
													handleColorChange(status, e.target.value)
												}
												className="w-12 h-10 p-1 cursor-pointer rounded"
											/>
											<Label className="text-xs md:text-sm flex-1">
												{t(
													`orgSettings.features.attendance.colorLegend.statuses.${status}`,
												)}
											</Label>
											<div
												className="px-3 py-1.5 rounded text-xs font-medium min-w-16 text-center"
												style={{
													backgroundColor: colorLegend[status],
													color: getContrastingTextColor(colorLegend[status]),
												}}
											>
												Preview
											</div>
										</div>
									))}
								</div>

								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleResetColors}
									className="mt-2"
								>
									<RotateCcw className="h-4 w-4 mr-2" />
									{t(
										"orgSettings.features.attendance.colorLegend.resetToDefault",
									)}
								</Button>
							</div>

							{/* Enrollment Status Filter Section */}
							<div className="space-y-3 md:space-y-4 pt-4 border-t border-base-300">
								<div>
									<Label className="text-sm md:text-base font-medium">
										{t(
											"orgSettings.features.attendance.enrollmentStatusFilter.title",
										)}
									</Label>
									<p className="text-xs md:text-sm text-base-content/60 mt-1">
										{t(
											"orgSettings.features.attendance.enrollmentStatusFilter.description",
										)}
									</p>
								</div>

								<div className="space-y-3">
									<div>
										<Label className="text-xs md:text-sm font-medium">
											{t(
												"orgSettings.features.attendance.enrollmentStatusFilter.defaultStatuses",
											)}
										</Label>
										<p className="text-xs text-base-content/60 mt-1">
											{t(
												"orgSettings.features.attendance.enrollmentStatusFilter.defaultStatusesDescription",
											)}
										</p>
									</div>

									<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
										{Object.values(EnrollmentStatus).map((status) => (
											<div key={status} className="flex items-center gap-2">
												<Checkbox
													id={`filter-status-${status}`}
													checked={enrollmentStatusFilter.defaultStatuses.includes(
														status,
													)}
													onCheckedChange={() =>
														handleDefaultStatusToggle(status)
													}
												/>
												<Label
													htmlFor={`filter-status-${status}`}
													className="text-xs md:text-sm cursor-pointer"
												>
													{t(
														`orgSettings.features.attendance.enrollmentStatusFilter.statuses.${status}`,
													)}
												</Label>
											</div>
										))}
									</div>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Schedule Settings Card */}
					<Card className="mb-4 md:mb-6">
						<CardHeader className="px-3 md:px-6 pt-3 md:pt-6 pb-2 md:pb-4">
							<CardTitle className="flex items-center space-x-2 text-sm md:text-base">
								<Settings className="h-4 w-4 md:h-5 md:w-5 text-primary" />
								<span>{t("orgSettings.features.schedule.title")}</span>
							</CardTitle>
							<CardDescription className="text-xs md:text-sm">
								{t("orgSettings.features.schedule.description")}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4 md:space-y-6 px-3 md:px-6 pb-3 md:pb-6">
							<div className="space-y-3 md:space-y-4">
								<div>
									<Label className="text-sm md:text-base font-medium">
										{t("orgSettings.features.schedule.overlapValidation.title")}
									</Label>
									<p className="text-xs md:text-sm text-base-content/60 mt-1">
										{t(
											"orgSettings.features.schedule.overlapValidation.description",
										)}
									</p>
								</div>

								<RadioGroup
									value={scheduleOverlapMode}
									onValueChange={(value) =>
										setScheduleOverlapMode(
											value as FeatFlagScheduleOverlapValidation,
										)
									}
									className="space-y-3 md:space-y-4"
								>
									{Object.values(FeatFlagScheduleOverlapValidation).map(
										(mode) => (
											<div
												key={mode}
												className="flex items-start space-x-2 md:space-x-3"
											>
												<RadioGroupItem
													value={mode}
													id={`schedule-overlap-${mode}`}
													className="mt-0.5 md:mt-1"
												/>
												<div className="flex-1 min-w-0">
													<Label
														htmlFor={`schedule-overlap-${mode}`}
														className="text-xs md:text-sm font-medium cursor-pointer"
													>
														{t(
															`orgSettings.features.schedule.overlapValidation.modes.${mode}`,
														)}
													</Label>
													<p className="text-xs text-base-content/60 mt-0.5 md:mt-1">
														{t(
															`orgSettings.features.schedule.overlapValidation.modeDescriptions.${mode}`,
														)}
													</p>
												</div>
											</div>
										),
									)}
								</RadioGroup>
							</div>
						</CardContent>
					</Card>

					{/* Save Button */}
					<div className="flex justify-end">
						<Button
							onClick={handleSave}
							disabled={!hasChanges || isSaving}
							className="w-full md:w-auto md:min-w-32 text-primary-content"
						>
							{isSaving ? t("common.saving") : t("common.save")}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
